import {
  makeEquation,
  normalizeOperation,
  normalizeRational,
  type Equation,
  type AlgebraOperation,
} from '../../shared/algebra.ts';
import type { StageId } from '../../shared/types.ts';
import type { ReplySpeechMetadata } from './useTutorVoice.ts';
export type TutorPage = {
  id: string;
  title: string;
  text: string;
  stageId: StageId;
  panel: 'model' | 'solve' | 'check';
  equation: Equation;
  operation?: AlgebraOperation;
  candidate?: string;
  createdAt: number;
  teachingVersion?: 1;
  reflection?: { prompt: string; learnerAnswer: string };
  audio?: ReplySpeechMetadata & { url: string; createdAt: number };
};
export type PageDraft = Omit<TutorPage, 'id' | 'createdAt' | 'audio'>;
export function pageMatches(a: TutorPage, b: PageDraft) {
  return (
    a.text === b.text &&
    a.stageId === b.stageId &&
    a.panel === b.panel &&
    JSON.stringify(a.equation) === JSON.stringify(b.equation) &&
    JSON.stringify(a.operation) === JSON.stringify(b.operation) &&
    a.candidate === b.candidate &&
    JSON.stringify(a.reflection) === JSON.stringify(b.reflection)
  );
}
export function readNotebook(value: string | null): TutorPage[] {
  try {
    return (JSON.parse(value || '[]') as unknown[])
      .slice(0, 100)
      .flatMap((raw) => {
        try {
          const p = raw as TutorPage;
          if (
            !p ||
            typeof p.id !== 'string' ||
            p.id.length > 100 ||
            typeof p.title !== 'string' ||
            typeof p.text !== 'string' ||
            p.text.length > 6000 ||
            !['concept', 'application', 'transfer'].includes(p.stageId) ||
            !['model', 'solve', 'check'].includes(p.panel) ||
            !Number.isFinite(p.createdAt)
          )
            return [];
          const equation = makeEquation(
            p.equation.left.x,
            p.equation.left.constant,
            p.equation.right.x,
            p.equation.right.constant,
          );
          const operation = p.operation
            ? normalizeOperation(p.operation)
            : undefined;
          return [
            {
              id: p.id,
              title: p.title.slice(0, 120),
              text: p.text,
              stageId: p.stageId,
              panel: p.panel,
              equation,
              operation,
              candidate:
                typeof p.candidate === 'string' ? normalizeRational(p.candidate) : undefined,
              createdAt: p.createdAt,
              teachingVersion: p.teachingVersion === 1 ? 1 : undefined,
              reflection: p.reflection && typeof p.reflection.prompt === 'string' &&
                typeof p.reflection.learnerAnswer === 'string'
                ? { prompt: p.reflection.prompt.slice(0, 600), learnerAnswer: p.reflection.learnerAnswer.slice(0, 2000) }
                : undefined,
            },
          ];
        } catch {
          return [];
        }
      });
  } catch {
    return [];
  }
}
/** Keep the tutor's single invitation beside the answer field, even after audio ends. */
export function notebookQuestion(text: string): string | undefined {
  return text.match(/(?:^|[.!?]\s+)([^.!?]{1,240}\?)\s*$/)?.[1]?.trim();
}
/** Reopening never starts a countdown. Only completed playback may schedule one. */
export function mayCollapseNote(input: {
  playing: boolean;
  busy: boolean;
  pinned: boolean;
  interacting: boolean;
  remaining: number | null;
}) {
  return (
    !input.playing &&
    !input.busy &&
    !input.pinned &&
    !input.interacting &&
    input.remaining !== null &&
    input.remaining <= 0
  );
}
